// Solicitudes de información (Blueprint §9 + §31.1, VLT-259).
// El staff crea solicitudes con ítems (pregunta o documento); el cliente responde
// SOLO lo suyo (RLS 0007). La reconciliación básica vive aquí: cuando todos los
// ítems quedan satisfechos, la solicitud se completa y deja de recordar.
import { supabase, hasBackend } from '@/lib/supabase';
import { tasksService } from './tasks';
import { loadFailed } from './loadError';
import { sessionUser } from '@/lib/sessionUser';

export type ItemKind = 'question' | 'document';
/** Estados canónicos §31.1 (subset que la UI maneja hoy). */
export type ItemState =
  | 'required' | 'requested' | 'received' | 'verified'
  | 'needs_review' | 'rejected' | 'expired' | 'waived' | 'not_applicable';

export interface RequestItem {
  id: string;
  kind: ItemKind;
  label: string;
  /** Traducción aprobada al otro idioma (§34.3); los ítems libres no la llevan. */
  translatedLabel?: string | null;
  docType?: string | null;
  state: ItemState;
  /** §31.5: motivo del rechazo, visible para el cliente. */
  rejectedReason?: string | null;
  /** VLT-262: archivo que satisfizo el slot (nombre/tamaño; hash en metadata). */
  upload?: { name: string; sizeLabel: string; hash?: string; at?: string; key?: string } | null;
  answerText?: string | null;
  position: number;
}

export interface InfoRequest {
  id: string;
  clientId: string;
  title: string;
  /** Idioma en que redactó el staff ('en'|'es'); el cliente ve su versión. */
  sourceLanguage: string;
  translatedTitle?: string | null;
  instructions?: string | null;
  dueDate?: string | null;
  status: ItemState;
  sentAt?: string | null;
  items: RequestItem[];
  /** Secuencia de recordatorio (VLT-398): toques dados y por qué paró. */
  chase?: { count: number; lastAt: string | null; stopped: string | null } | null;
}

export interface NewRequestInput {
  clientId: string;
  title: string;
  translatedTitle?: string;
  sourceLanguage: string;
  instructions?: string;
  items: { kind: ItemKind; label: string; translatedLabel?: string; docType?: string }[];
}

/** §34.3/§34.5: el cliente ve la traducción aprobada si su idioma difiere del
 *  de redacción; sin traducción aprobada, se preserva el original (jamás se
 *  traduce a máquina un texto de alto riesgo). Pura, testeable. */
export function pickText(uiLang: string, sourceLang: string, original: string, translated?: string | null): string {
  if (uiLang.slice(0, 2) === sourceLang.slice(0, 2)) return original;
  return translated?.trim() ? translated : original;
}

/** Estados que cuentan como "ya no hay que perseguirlo" (suprime recordatorios). */
const SATISFIED: ItemState[] = ['received', 'verified', 'waived', 'not_applicable'];

// ─── Ciclo de estados §31.1 (VLT-259) ───────────────────────────────────────
// Lo que el STAFF puede hacer con cada ítem. Toda transición pasa por
// setItemState y queda auditada por los triggers de 0003 (before/after JSONB).
export const STAFF_TRANSITIONS: Record<ItemState, ItemState[]> = {
  required: ['requested', 'waived'],
  requested: ['received', 'expired', 'waived'],
  received: ['verified', 'needs_review'],
  needs_review: ['verified', 'rejected'],
  rejected: ['requested'],
  expired: ['requested'],
  verified: ['needs_review'],
  waived: ['requested'],
  not_applicable: ['requested'],
};

/** §31.5: rechazar o eximir JAMÁS va sin motivo escrito. */
export const NEEDS_REASON: ItemState[] = ['rejected', 'waived'];

export function canTransition(from: ItemState, to: ItemState): boolean {
  return STAFF_TRANSITIONS[from]?.includes(to) ?? false;
}

const isUploadMeta = (v: unknown): v is { name: string; sizeLabel: string; hash?: string; at?: string; key?: string } =>
  typeof v === 'object' && v !== null && typeof (v as { name?: unknown }).name === 'string' &&
  typeof (v as { sizeLabel?: unknown }).sizeLabel === 'string';

/** Reconciliación: estado de la solicitud derivado de sus ítems (pura, testeable). */
export function deriveRequestStatus(items: { state: ItemState }[]): ItemState {
  if (items.length === 0) return 'requested';
  if (items.every((i) => SATISFIED.includes(i.state))) return 'received';
  if (items.some((i) => i.state === 'needs_review')) return 'needs_review';
  return 'requested';
}

/** Ítems aún pendientes de un conjunto (los únicos que merecen recordatorio). */
export function pendingItems<T extends { state: ItemState }>(items: T[]): T[] {
  return items.filter((i) => !SATISFIED.includes(i.state));
}

export interface RequestsService {
  /** Solicitudes del cliente autenticado (rol client; RLS filtra). */
  listMine(): Promise<InfoRequest[]>;
  /** Solicitudes de un cliente (vista staff). */
  listForClient(clientId: string): Promise<InfoRequest[]>;
  /** Crea y envía una solicitud con sus ítems (staff). */
  create(input: NewRequestInput): Promise<{ ok: boolean; id?: string }>;
  /** El cliente responde un ítem tipo pregunta. */
  answerItem(requestId: string, itemId: string, answerText: string): Promise<boolean>;
  /** El staff mueve un ítem por el ciclo §31.1 (motivo obligatorio en rechazos/exenciones). */
  setItemState(requestId: string, itemId: string, to: ItemState, reason?: string): Promise<boolean>;
  /** VLT-262: el cliente sube al slot exacto — recibido + huella + reconciliación.
   *  El document_id real (bytes en almacenamiento) se enlaza en VLT-263. */
  markUploaded(requestId: string, itemId: string, upload: { name: string; sizeLabel: string; hash: string; key?: string }): Promise<boolean>;
}

// ─── Implementación real (Supabase, RLS por tenant) ─────────────────────────

interface Row {
  id: string; client_id: string; title: string; source_language: string;
  translated_title: string | null; instructions: string | null;
  due_date: string | null; status: ItemState; sent_at: string | null;
  metadata: Record<string, unknown> | null;
  request_items: {
    id: string; kind: ItemKind; label: string; translated_label: string | null;
    doc_type: string | null; state: ItemState; answer_text: string | null; position: number;
    metadata: Record<string, unknown> | null;
  }[];
}

const SELECT = 'id, client_id, title, source_language, translated_title, instructions, due_date, status, sent_at, metadata, request_items(id, kind, label, translated_label, doc_type, state, answer_text, position, metadata)';

/** metadata.chase → contrato de UI (lo escribe el job document-chasers). */
function readChase(meta: Record<string, unknown> | null | undefined): InfoRequest['chase'] {
  const c = meta?.chase as { touches?: string[]; stopped?: string } | undefined;
  if (!c || !Array.isArray(c.touches) || c.touches.length === 0) return null;
  return {
    count: c.touches.length,
    lastAt: c.touches[c.touches.length - 1]?.slice(0, 10) ?? null,
    stopped: typeof c.stopped === 'string' ? c.stopped : null,
  };
}

const rowToRequest = (r: Row): InfoRequest => ({
  id: r.id, clientId: r.client_id, title: r.title, sourceLanguage: r.source_language,
  translatedTitle: r.translated_title, instructions: r.instructions,
  dueDate: r.due_date, status: r.status, sentAt: r.sent_at,
  chase: readChase(r.metadata),
  items: [...r.request_items]
    .sort((a, b) => a.position - b.position)
    .map((i) => ({
      id: i.id, kind: i.kind, label: i.label, translatedLabel: i.translated_label,
      docType: i.doc_type, state: i.state, answerText: i.answer_text, position: i.position,
      // §31.5: el motivo del rechazo se le MUESTRA al cliente — sin explicación no hay corrección.
      rejectedReason: typeof i.metadata?.rejected_reason === 'string' ? i.metadata.rejected_reason : null,
      upload: isUploadMeta(i.metadata?.upload) ? i.metadata.upload : null,
    })),
});

const fetchRequests = async (clientId?: string): Promise<InfoRequest[]> => {
  let q = supabase.from('information_requests').select(SELECT).order('created_at', { ascending: false }).limit(100);
  if (clientId) q = q.eq('client_id', clientId);
  const { data, error } = await q;
  if (error) loadFailed('requests.list', error);
  return (data as unknown as Row[]).map(rowToRequest);
};

const realRequests: RequestsService = {
  listMine: () => fetchRequests(),
  listForClient: (clientId) => fetchRequests(clientId),

  async create({ clientId, title, translatedTitle, sourceLanguage, instructions, items }) {
    // La organización sale del perfil del staff autenticado (nunca del formulario).
    const { data: auth } = await sessionUser();
    if (!auth.user) return { ok: false };
    const { data: prof } = await supabase.from('profiles').select('organization_id').eq('id', auth.user.id).maybeSingle();
    const orgId = (prof as { organization_id: string } | null)?.organization_id;
    if (!orgId) return { ok: false };

    const { data: req, error } = await supabase
      .from('information_requests')
      .insert({ organization_id: orgId, client_id: clientId, title, translated_title: translatedTitle ?? null, source_language: sourceLanguage, instructions, sent_at: new Date().toISOString() } as never)
      .select('id')
      .single();
    if (error || !req) {
      if (error) console.warn('[VaulTex] requests.create:', error.message);
      return { ok: false };
    }
    const reqId = (req as { id: string }).id;
    const { error: e2 } = await supabase.from('request_items').insert(
      items.map((it, idx) => ({
        organization_id: orgId, request_id: reqId, kind: it.kind,
        label: it.label, translated_label: it.translatedLabel ?? null,
        doc_type: it.docType ?? null, position: idx,
      })) as never,
    );
    if (e2) {
      console.warn('[VaulTex] requests.items:', e2.message);
      return { ok: false };
    }
    return { ok: true, id: reqId };
  },

  async answerItem(requestId, itemId, answerText) {
    const { error } = await supabase
      .from('request_items')
      .update({ answer_text: answerText, state: 'received', satisfied_at: new Date().toISOString() } as never)
      .eq('id', itemId);
    if (error) {
      console.warn('[VaulTex] requests.answer:', error.message);
      return false;
    }
    // Reconciliación: si ya no queda nada pendiente, la solicitud se completa.
    const { data } = await supabase.from('request_items').select('state').eq('request_id', requestId);
    const states = (data as { state: ItemState }[] | null) ?? [];
    const status = deriveRequestStatus(states);
    if (status === 'received') {
      await supabase
        .from('information_requests')
        .update({ status, completed_at: new Date().toISOString() } as never)
        .eq('id', requestId);
    }
    return true;
  },

  async markUploaded(requestId, itemId, upload) {
    const { data: cur } = await supabase.from('request_items').select('state, metadata').eq('id', itemId).maybeSingle();
    const row = cur as { state: ItemState; metadata: Record<string, unknown> } | null;
    if (!row || SATISFIED.includes(row.state)) return false;
    const { error } = await supabase
      .from('request_items')
      .update({
        state: 'received',
        satisfied_at: new Date().toISOString(),
        metadata: { ...row.metadata, rejected_reason: null, upload: { ...upload, at: new Date().toISOString() } },
      } as never)
      .eq('id', itemId);
    if (error) {
      console.warn('[VaulTex] requests.markUploaded:', error.message);
      return false;
    }
    const { data } = await supabase.from('request_items').select('state').eq('request_id', requestId);
    const states = (data as { state: ItemState }[] | null) ?? [];
    const status = deriveRequestStatus(states);
    if (status === 'received') {
      await supabase
        .from('information_requests')
        .update({ status, completed_at: new Date().toISOString() } as never)
        .eq('id', requestId);
    }
    return true;
  },

  async setItemState(requestId, itemId, to, reason) {
    if (NEEDS_REASON.includes(to) && !reason?.trim()) return false;
    // La regla se valida contra el estado REAL del ítem, no contra la UI.
    const { data: cur } = await supabase.from('request_items').select('state, metadata, label').eq('id', itemId).maybeSingle();
    const row = cur as { state: ItemState; metadata: Record<string, unknown>; label: string } | null;
    if (!row || !canTransition(row.state, to)) return false;

    const { data: auth } = await sessionUser();
    const satisfied = SATISFIED.includes(to);
    const patch: Record<string, unknown> = {
      state: to,
      satisfied_at: satisfied ? new Date().toISOString() : null,
    };
    if (to === 'waived') {
      patch.waived_by = auth.user?.id ?? null;
      patch.waived_reason = reason?.trim();
    }
    if (to === 'rejected') {
      // §31.5: el motivo del rechazo queda con el ítem (no hay columna propia).
      patch.metadata = { ...row.metadata, rejected_reason: reason?.trim() };
    }
    const { error } = await supabase.from('request_items').update(patch as never).eq('id', itemId);
    if (error) {
      console.warn('[VaulTex] requests.setItemState:', error.message);
      return false;
    }
    // Reconciliación del padre (misma regla que answerItem).
    const { data } = await supabase.from('request_items').select('state').eq('request_id', requestId);
    const states = (data as { state: ItemState }[] | null) ?? [];
    const status = deriveRequestStatus(states);
    await supabase
      .from('information_requests')
      .update({ status, completed_at: status === 'received' ? new Date().toISOString() : null } as never)
      .eq('id', requestId);
    // Orquestación (§21/§22): lo dudoso genera tarea en la cola del preparador
    // — best-effort: si falla, la transición NO se revierte.
    if (to === 'needs_review') {
      void tasksService.create({
        title: `Revisar «${row.label}»`, priority: 'high', queueRole: 'tax_preparer', source: 'workflow',
      });
    }
    return true;
  },
};

// ─── Implementación demo (fixtures) ─────────────────────────────────────────

const DEMO: InfoRequest[] = [
  {
    id: 'r1', clientId: 'demo', title: 'Documents for your 2025 tax return',
    sourceLanguage: 'en', translatedTitle: 'Documentos para tu declaración 2025',
    instructions: 'We need these to get started.',
    dueDate: '2026-09-15', status: 'requested', sentAt: '2026-08-25',
    chase: { count: 2, lastAt: '2026-09-03', stopped: null },
    items: [
      { id: 'i1', kind: 'document', label: 'Valid photo ID', translatedLabel: 'Identificación vigente (foto)', docType: 'ID', state: 'received', position: 0 },
      { id: 'i2', kind: 'document', label: 'W-2 from your employer', translatedLabel: 'W-2 de tu empleador', docType: 'W-2', state: 'requested', position: 1 },
      { id: 'i3', kind: 'question', label: 'Did your address change this year?', translatedLabel: '¿Cambiaste de dirección este año?', state: 'requested', position: 2 },
      { id: 'i4', kind: 'document', label: 'Proof of address', translatedLabel: 'Comprobante de domicilio', docType: 'utility-bill', state: 'rejected', rejectedReason: 'La foto no se lee bien. ¿Puedes subirla de nuevo?', position: 3 },
    ],
  },
];

// Copia profunda: si devolviéramos la misma referencia, React no re-renderiza.
const snapshot = (): InfoRequest[] => JSON.parse(JSON.stringify(DEMO)) as InfoRequest[];

const fixtureRequests: RequestsService = {
  listMine: () => Promise.resolve(snapshot()),
  listForClient: () => Promise.resolve(snapshot()),
  create: () => Promise.resolve({ ok: true, id: 'demo' }),
  answerItem: (_reqId, itemId, answer) => {
    const it = DEMO[0].items.find((i) => i.id === itemId);
    if (it) {
      it.answerText = answer;
      it.state = 'received';
      it.rejectedReason = null; // la corrección reemplaza al rechazo
      DEMO[0].status = deriveRequestStatus(DEMO[0].items);
    }
    return Promise.resolve(true);
  },
  markUploaded: (_reqId, itemId, upload) => {
    const it = DEMO[0].items.find((i) => i.id === itemId);
    if (!it || ['received', 'verified', 'waived', 'not_applicable'].includes(it.state)) return Promise.resolve(false);
    it.state = 'received';
    it.rejectedReason = null;
    // El hash viaja también en demo: la detección de duplicados (VLT-351) lo usa.
    it.upload = { name: upload.name, sizeLabel: upload.sizeLabel, hash: upload.hash, key: upload.key, at: new Date().toISOString() };
    DEMO[0].status = deriveRequestStatus(DEMO[0].items);
    return Promise.resolve(true);
  },
  setItemState: (_reqId, itemId, to, reason) => {
    if (NEEDS_REASON.includes(to) && !reason?.trim()) return Promise.resolve(false);
    const it = DEMO[0].items.find((i) => i.id === itemId);
    if (!it || !canTransition(it.state, to)) return Promise.resolve(false);
    it.state = to;
    it.rejectedReason = to === 'rejected' ? reason?.trim() : null;
    DEMO[0].status = deriveRequestStatus(DEMO[0].items);
    if (to === 'needs_review') {
      void tasksService.create({ title: `Revisar «${it.label}»`, priority: 'high', queueRole: 'tax_preparer', source: 'workflow' });
    }
    return Promise.resolve(true);
  },
};

export const requestsService: RequestsService = hasBackend ? realRequests : fixtureRequests;

/** Plantillas rápidas del preparador (§9) con TRADUCCIÓN APROBADA (§34.3):
 *  textos de alto riesgo jamás pasan por traducción automática — cada plantilla
 *  lleva sus dos versiones revisadas por la firma. */
export interface TemplateItem { kind: ItemKind; en: string; es: string; docType?: string }
export const REQUEST_TEMPLATES: { key: string; items: TemplateItem[] }[] = [
  { key: 'identity', items: [
    { kind: 'document', en: 'Government ID (photo)', es: 'Identificación oficial (foto)', docType: 'ID' },
    { kind: 'document', en: 'Social Security card', es: 'Tarjeta de Seguro Social', docType: 'SSN' },
  ]},
  { key: 'w2', items: [{ kind: 'document', en: 'W-2', es: 'W-2', docType: 'W-2' }] },
  { key: 'scheduleC', items: [
    { kind: 'question', en: 'Vehicle purchase date', es: 'Fecha de compra del vehículo' },
    { kind: 'question', en: 'Purchase price', es: 'Precio de compra' },
    { kind: 'question', en: 'New or used?', es: '¿Nuevo o usado?' },
    { kind: 'question', en: 'Total miles this year', es: 'Millas totales del año' },
    { kind: 'question', en: 'Business miles', es: 'Millas de negocio' },
    { kind: 'question', en: 'Available for personal use?', es: '¿Disponible para uso personal?' },
  ]},
  { key: 'mortgage', items: [{ kind: 'document', en: '1098 — Mortgage interest', es: '1098 — Intereses de hipoteca', docType: '1098' }] },
  { key: 'dependents', items: [
    { kind: 'document', en: 'Dependent Social Security card', es: 'Tarjeta de Seguro Social del dependiente', docType: 'SSN' },
    { kind: 'question', en: 'Months lived with you in the year', es: 'Meses que vivió contigo en el año' },
  ]},
  // VAU-REQ-008 (Build Spec): los 7 casos recurrentes completos.
  { key: 'homeOffice', items: [
    { kind: 'question', en: 'Office area (sq ft)', es: 'Área de la oficina (pies²)' },
    { kind: 'question', en: 'Total home area (sq ft)', es: 'Área total de la vivienda (pies²)' },
    { kind: 'question', en: 'Do you rent or own the home?', es: '¿La vivienda es rentada o propia?' },
  ]},
  { key: 'rental', items: [
    { kind: 'question', en: 'Rental property address', es: 'Dirección de la propiedad rentada' },
    { kind: 'question', en: 'Rents received in the year', es: 'Rentas recibidas en el año' },
    { kind: 'question', en: 'Expenses: repairs, insurance, property tax', es: 'Gastos: reparaciones, seguro, impuesto a la propiedad' },
    { kind: 'document', en: '1098 — Mortgage interest (if financed)', es: '1098 — Intereses de hipoteca (si está financiada)', docType: '1098' },
  ]},
  { key: 'education', items: [
    { kind: 'document', en: '1098-T: Tuition statement', es: '1098-T: Constancia de matrícula', docType: '1098-T' },
    { kind: 'question', en: 'Student name', es: 'Nombre del estudiante' },
    { kind: 'document', en: 'Receipts for books and materials', es: 'Recibos de libros y materiales' },
  ]},
];

/** Convierte una plantilla a ítems de solicitud según el idioma de redacción. */
export function templateToItems(items: TemplateItem[], sourceLang: string): NewRequestInput['items'] {
  const src = sourceLang.slice(0, 2) === 'es' ? 'es' : 'en';
  const other = src === 'es' ? 'en' : 'es';
  return items.map((it) => ({ kind: it.kind, label: it[src], translatedLabel: it[other], docType: it.docType }));
}
